import { Copy } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

// Copy-pasteable curl/python/JS examples for validating the agent over A2A,
// rendered on the Connection tab once an agent is saved.

interface IntegrationSnippetsProps {
  endpointUrl: string;
  agentCardUrl: string;
  /** Either the just-minted bearer (if shown once on this page load) or a
   *  placeholder string the user is expected to substitute. */
  bearerForExamples: string;
  /** True when ``bearerForExamples`` is the real, freshly-minted token. */
  bearerIsLive: boolean;
  onCopy: (text: string) => void;
}

export function IntegrationSnippets({
  endpointUrl,
  agentCardUrl,
  bearerForExamples,
  bearerIsLive,
  onCopy,
}: IntegrationSnippetsProps) {
  if (!endpointUrl || !agentCardUrl) return null;

  // Pretty-print the JSON-RPC body so curl --data-raw renders cleanly when
  // pasted into a shell. Keep ascii-safe so bash doesn't choke on quotes.
  const listWorkflowsBody = JSON.stringify(
    {
      jsonrpc: '2.0',
      id: 'req-1',
      method: 'SendMessage',
      params: {
        message: {
          messageId: 'msg-list-1',
          role: 'user',
          parts: [{ data: { skill_id: 'list_workflows' } }],
        },
      },
    },
    null,
    2,
  );

  const callWorkflowBody = JSON.stringify(
    {
      jsonrpc: '2.0',
      id: 'req-2',
      method: 'SendMessage',
      params: {
        message: {
          messageId: 'msg-call-1',
          role: 'user',
          parts: [
            {
              data: {
                skill_id: 'call_workflow',
                workflow_id: '<workflow_id-from-list_workflows>',
                inputs: {
                  /* fill with the workflow's input shape (see
                     get_workflow_details for the per-node field map) */
                },
                idempotency_key: 'demo-key-001',
              },
            },
          ],
        },
      },
    },
    null,
    2,
  );

  // Streaming live-event tail: SendStreamingMessage returns
  // text/event-stream, one JSON-RPC frame per ``data:`` line.
  const subscribeEventsBody = JSON.stringify(
    {
      jsonrpc: '2.0',
      id: 'req-3',
      method: 'SendStreamingMessage',
      params: {
        message: {
          messageId: 'msg-sub-1',
          role: 'user',
          parts: [
            {
              data: {
                skill_id: 'subscribe_workflow_events',
                run_id: '<run_id-from-call_workflow-response>',
                since_sequence: 0,
                types: [
                  'node.thinking',
                  'node.agent_message',
                  'node.tool_call',
                  'node.tool_result',
                ],
                inactivity_timeout_seconds: 600,
              },
            },
          ],
        },
      },
    },
    null,
    2,
  );

  // ===== curl =====
  const curlAgentCard = [
    `curl -sS \\`,
    `  -H "Authorization: Bearer ${bearerForExamples}" \\`,
    `  ${agentCardUrl}`,
  ].join('\n');

  const curlListWorkflows = [
    `curl -sS \\`,
    `  -X POST \\`,
    `  -H "Authorization: Bearer ${bearerForExamples}" \\`,
    `  -H "Content-Type: application/json" \\`,
    `  -d '${listWorkflowsBody}' \\`,
    `  ${endpointUrl}`,
  ].join('\n');

  const curlCallWorkflow = [
    `curl -sS \\`,
    `  -X POST \\`,
    `  -H "Authorization: Bearer ${bearerForExamples}" \\`,
    `  -H "Content-Type: application/json" \\`,
    `  -d '${callWorkflowBody}' \\`,
    `  ${endpointUrl}`,
  ].join('\n');

  // -N keeps stdout unbuffered so SSE frames stream live; without it
  // curl batches output and the demo looks frozen.
  const curlSubscribeEvents = [
    `curl -sS -N \\`,
    `  -X POST \\`,
    `  -H "Authorization: Bearer ${bearerForExamples}" \\`,
    `  -H "Content-Type: application/json" \\`,
    `  -H "Accept: text/event-stream" \\`,
    `  -d '${subscribeEventsBody}' \\`,
    `  ${endpointUrl}`,
  ].join('\n');

  // ===== python =====
  // Demonstrates the full lifecycle: discover -> list -> invoke ->
  // tail the live event stream until terminal.  Uses httpx for both
  // POST + streaming SSE.
  const pythonExample = `import httpx, json

# A2A v1.0-compliant client (matches the canonical spec at
# https://github.com/a2aproject/A2A and what 'a2a-sdk' 1.0.x emits
# natively).  Capitol's gateway accepts the v0.3 and pre-v0.3 envelope
# shapes too -- see SCALE_INTEGRATION_HANDOFF.md for the back-compat
# matrix.

ENDPOINT  = "${endpointUrl}"
CARD_URL  = "${agentCardUrl}"
BEARER    = "${bearerForExamples}"  # rotate via the UI; never commit
HEADERS   = {"Authorization": f"Bearer {BEARER}", "Content-Type": "application/json"}

TERMINAL_STATES = {
    "TASK_STATE_COMPLETED", "TASK_STATE_FAILED",
    "TASK_STATE_CANCELED", "TASK_STATE_REJECTED",
}

# 1. Discover the agent's skills (also confirms auth + DNS + kill-switch).
card = httpx.get(CARD_URL, headers=HEADERS).json()
print("skills:", [s["id"] for s in card["skills"]])

# 2. List the workflows this agent advertises as A2A skills.
# Wire shape: v1.0 canonical (PascalCase method, no params.id, no
# part type discriminator, messageId on the message).
resp = httpx.post(ENDPOINT, headers=HEADERS, json={
    "jsonrpc": "2.0",
    "id": "req-1",
    "method": "SendMessage",            # v0.3 alias "message/send" also accepted
    "params": {
        "message": {
            "messageId": "msg-list-1",  # required by SDK 1.0.x for dedupe
            "role": "user",
            "parts": [{"data": {"skill_id": "list_workflows"}}],
        },
    },
}).json()
print(json.dumps(resp, indent=2))

# 3. Invoke a workflow by its id (from step 2).
# - Skill args go directly under "data" -- no "input" wrapper.
# - idempotency_key lets you safely retry the same request without
#   double-triggering the workflow within a 24h window.
# - Pass message.contextId if you want to thread this call into the
#   agent's conversation memory (multi-turn); omit for one-shot calls.
run = httpx.post(ENDPOINT, headers=HEADERS, json={
    "jsonrpc": "2.0",
    "id": "req-2",
    "method": "SendMessage",
    "params": {
        "message": {
            "messageId": "msg-call-1",
            "role": "user",
            "parts": [{
                "data": {
                    "skill_id": "call_workflow",
                    "workflow_id": "<workflow_id-from-list_workflows>",
                    "inputs": {},          # workflow input shape from get_workflow_details
                    "idempotency_key": "demo-key-001",
                },
            }],
        },
    },
}).json()
run_id = run["result"]["id"]
print("run_id:", run_id)

# 4. (Optional) Live-tail the chain-of-thought + node events for this run.
# Uses "SendStreamingMessage" (v0.3 alias: "message/stream"; pre-v0.3:
# "tasks/sendSubscribe") + Accept: text/event-stream.  Each frame is
# one JSON-RPC envelope on a single "data:" line.  Per A2A v1.0 spec
# the "kind" discriminator and "final" boolean are removed -- the
# terminal frame is whichever TaskStatusUpdateEvent has
# status.state ∈ TERMINAL_STATES, and the gateway closes the SSE
# connection after sending it.
sub_body = {
    "jsonrpc": "2.0",
    "id": "req-3",
    "method": "SendStreamingMessage",
    "params": {
        "message": {
            "messageId": "msg-sub-1",
            "role": "user",
            "parts": [{
                "data": {
                    "skill_id": "subscribe_workflow_events",
                    "run_id": run_id,
                    "since_sequence": 0,
                    "types": ["node.thinking", "node.agent_message",
                              "node.tool_call", "node.tool_result"],
                },
            }],
        },
    },
}
sse_headers = {**HEADERS, "Accept": "text/event-stream"}
with httpx.stream("POST", ENDPOINT, headers=sse_headers, json=sub_body, timeout=None) as r:
    for line in r.iter_lines():
        if not line or not line.startswith("data:"):
            continue
        frame = json.loads(line[5:].strip())
        result = frame.get("result") or {}
        # Discriminate by JSON member presence (v1.0 spec: no "kind" field).
        if "artifact" in result:
            # Event frame: artifact carries the WorkflowEvent envelope.
            evt = result["artifact"]["parts"][0]["data"]
            print(f'[seq={evt["sequence"]}] {evt["event_type"]}: {evt.get("data", {})}')
        elif "status" in result and result["status"].get("state") in TERMINAL_STATES:
            print("terminal:", result["status"]["state"])
            break
`;

  // ===== node / fetch =====
  // Same lifecycle as the Python version, in browser-/Node-style fetch.
  const jsExample = `// A2A v1.0-compliant client (matches the canonical spec at
// https://github.com/a2aproject/A2A and what \`a2a-sdk\` 1.0.x emits
// natively). Capitol's gateway accepts the v0.3 and pre-v0.3
// envelope shapes too -- see SCALE_INTEGRATION_HANDOFF.md for the
// back-compat matrix.

const ENDPOINT  = "${endpointUrl}";
const CARD_URL  = "${agentCardUrl}";
const BEARER    = "${bearerForExamples}";  // rotate via the UI; never commit

const headers = {
  Authorization: \`Bearer \${BEARER}\`,
  "Content-Type": "application/json",
};

const TERMINAL_STATES = new Set([
  "TASK_STATE_COMPLETED", "TASK_STATE_FAILED",
  "TASK_STATE_CANCELED", "TASK_STATE_REJECTED",
]);

// 1. Discover the agent's skills (also confirms auth + DNS + kill-switch).
const card = await fetch(CARD_URL, { headers }).then((r) => r.json());
console.log("skills:", card.skills.map((s) => s.id));

// 2. List the workflows this agent advertises as A2A skills.
// Wire shape: A2A v1.0 canonical (PascalCase method, no params.id,
// no part type discriminator, messageId on the message).
const list = await fetch(ENDPOINT, {
  method: "POST",
  headers,
  body: JSON.stringify({
    jsonrpc: "2.0",
    id: "req-1",
    method: "SendMessage",  // v0.3 alias "message/send" also accepted
    params: {
      message: {
        messageId: "msg-list-1",  // required by SDK 1.0.x for dedupe
        role: "user",
        parts: [{ data: { skill_id: "list_workflows" } }],
      },
    },
  }),
}).then((r) => r.json());
console.log(JSON.stringify(list, null, 2));

// 3. Invoke a workflow by its id (from step 2).
// - Skill args go directly under "data" -- no "input" wrapper.
// - idempotency_key lets you safely retry without double-triggering
//   the workflow within a 24h window.
// - Pass message.contextId to thread this call into the agent's
//   conversation memory (multi-turn); omit for one-shot calls.
const run = await fetch(ENDPOINT, {
  method: "POST",
  headers,
  body: JSON.stringify({
    jsonrpc: "2.0",
    id: "req-2",
    method: "SendMessage",
    params: {
      message: {
        messageId: "msg-call-1",
        role: "user",
        parts: [{
          data: {
            skill_id: "call_workflow",
            workflow_id: "<workflow_id-from-list_workflows>",
            inputs: {},  // workflow's input shape from get_workflow_details
            idempotency_key: "demo-key-001",
          },
        }],
      },
    },
  }),
}).then((r) => r.json());
const runId = run.result.id;
console.log("run_id:", runId);

// 4. (Optional) Live-tail the chain-of-thought + node events for this run.
// Uses "SendStreamingMessage" + Accept: text/event-stream. Each
// frame is one JSON-RPC envelope on a single "data:" line.  Per A2A
// v1.0 spec, no "kind" discriminator and no "final" boolean --
// discriminate by member presence (artifact vs status), and detect
// terminal by status.state membership in TERMINAL_STATES.
const sseResp = await fetch(ENDPOINT, {
  method: "POST",
  headers: { ...headers, Accept: "text/event-stream" },
  body: JSON.stringify({
    jsonrpc: "2.0",
    id: "req-3",
    method: "SendStreamingMessage",
    params: {
      message: {
        messageId: "msg-sub-1",
        role: "user",
        parts: [{
          data: {
            skill_id: "subscribe_workflow_events",
            run_id: runId,
            since_sequence: 0,
            types: ["node.thinking", "node.agent_message",
                    "node.tool_call", "node.tool_result"],
          },
        }],
      },
    },
  }),
});

const reader = sseResp.body.getReader();
const decoder = new TextDecoder();
let buf = "";
while (true) {
  const { value, done } = await reader.read();
  if (done) break;
  buf += decoder.decode(value, { stream: true });
  for (const line of buf.split(/\\r?\\n/)) {
    if (!line.startsWith("data:")) continue;
    const frame = JSON.parse(line.slice(5).trim());
    const result = frame.result || {};
    // v1.0 discrimination by JSON member presence:
    if ("artifact" in result) {
      const evt = result.artifact.parts[0].data;
      console.log(\`[seq=\${evt.sequence}] \${evt.event_type}\`, evt.data);
    } else if ("status" in result && TERMINAL_STATES.has(result.status?.state)) {
      console.log("terminal:", result.status.state);
      reader.cancel();
      break;
    }
  }
  buf = buf.split(/\\r?\\n/).pop() ?? "";  // keep partial trailing line
}
`;

  return (
    <section className='space-y-3'>
      <div className='flex items-center justify-between'>
        <h2 className='text-sm font-medium text-muted-foreground uppercase tracking-wide'>
          Try it
        </h2>
        <span className='text-[11px] text-muted-foreground'>
          Validate the agent over A2A from your terminal.
        </span>
      </div>

      {!bearerIsLive && (
        <div className='rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-400'>
          The bearer token is hidden once it&apos;s been issued - the snippets
          below use a placeholder. Replace{' '}
          <code className='font-mono'>{bearerForExamples}</code> with your
          stored bearer, or click <strong>Rotate</strong> above to mint a fresh
          one (this <em>invalidates</em> the previous bearer).
        </div>
      )}

      <Tabs defaultValue='curl' className='w-full'>
        <TabsList>
          <TabsTrigger value='curl'>curl</TabsTrigger>
          <TabsTrigger value='python'>python</TabsTrigger>
          <TabsTrigger value='js'>node / fetch</TabsTrigger>
        </TabsList>

        <TabsContent value='curl' className='space-y-3 pt-2'>
          <SnippetBlock
            title='1. Discover skills (agent card)'
            help='Fetches `/.well-known/agent-card.json`. A 200 with a skills list confirms auth + DNS + the kill-switch flag are all healthy. A 401 means the bearer is wrong; a 403 means `exposed_via_a2a` is off; a 404 means the URL or agent_id is wrong. The card also carries the live `errors` catalog, `wire_schema_version`, and per-workflow `inputSchema` for every auto-derived `run__*` skill.'
            code={curlAgentCard}
            onCopy={onCopy}
          />
          <SnippetBlock
            title='2. List allowlisted workflows'
            help='Round-trips the simplest read skill (`list_workflows`). Returns `{ "workflows": [...] }` with whatever this agent advertises. Empty list with no error = nothing in the allowlist or no matching tagged workflows in the org. Uses the canonical A2A v1.0 method `SendMessage` (v0.3 aliases `message/send` / pre-v0.3 `tasks/send` still accepted).'
            code={curlListWorkflows}
            onCopy={onCopy}
          />
          <SnippetBlock
            title='3. Invoke a workflow (call_workflow)'
            help='Substitute `workflow_id` from step 2 and the workflow input shape (introspect with `get_workflow_details`). Pass `idempotency_key` when retrying so you do not double-trigger within the 24h dedupe window. Returns a `Task` in `TASK_STATE_SUBMITTED` whose `id` is the Capitol `run_id` -- reuse it for polling (A2A v1.0 `GetTask`, alias `tasks/get`) or streaming (step 4).'
            code={curlCallWorkflow}
            onCopy={onCopy}
          />
          <SnippetBlock
            title='4. (Optional) Live-stream chain-of-thought'
            help='Opens an SSE stream for the run from step 3. Each frame is one A2A v1.0 envelope (camelCase keys, no `kind`/`final` fields) wrapping a `WorkflowEvent`. The `types` filter narrows to the four CoT events; omit it for the full event stream (lifecycle + CoT). `since_sequence` is the resume cursor -- pass the last `sequence` you saw on reconnect to backfill from persistence without duplicates. Uses A2A v1.0 method `SendStreamingMessage` (v0.3 aliases `message/stream` / pre-v0.3 `tasks/sendSubscribe` also accepted). The `-N` flag keeps curl unbuffered so frames stream live.'
            code={curlSubscribeEvents}
            onCopy={onCopy}
          />
        </TabsContent>

        <TabsContent value='python' className='pt-2'>
          <SnippetBlock
            title='Python (httpx)'
            help='Drop-in script - requires httpx (`pip install httpx`).'
            code={pythonExample}
            onCopy={onCopy}
          />
        </TabsContent>

        <TabsContent value='js' className='pt-2'>
          <SnippetBlock
            title='Node / browser (fetch)'
            help='Works in Node 18+ and modern browsers. CORS is open by default on the gateway; tighten via AGENTIC_BACKEND_CORS_ORIGINS for prod.'
            code={jsExample}
            onCopy={onCopy}
          />
        </TabsContent>
      </Tabs>
    </section>
  );
}

interface SnippetBlockProps {
  title: string;
  help: string;
  code: string;
  onCopy: (text: string) => void;
}

function SnippetBlock({ title, help, code, onCopy }: SnippetBlockProps) {
  return (
    <div className='rounded-md border bg-muted/30'>
      <div className='flex items-start justify-between px-3 py-2 border-b border-border/60'>
        <div className='space-y-0.5'>
          <p className='text-xs font-medium'>{title}</p>
          <p className='text-[11px] text-muted-foreground leading-snug'>
            {help}
          </p>
        </div>
        <Button
          size='sm'
          variant='ghost'
          className='shrink-0 h-7'
          onClick={() => onCopy(code)}
        >
          <Copy className='h-3 w-3 mr-1' />
          Copy
        </Button>
      </div>
      <pre className='overflow-x-auto px-3 py-2 text-xs font-mono leading-relaxed whitespace-pre'>
        {code}
      </pre>
    </div>
  );
}
